import { Button } from '@/components/Atoms/Button/Button';
import popoverStyles from '@/components/Atoms/Tooltip/PopoverContent.module.css';
import BellActive from '@/components/Icons/BellActive';
import BellInactive from '@/components/Icons/BellInactive';
import { CsrfForm } from '@/components/Molecules/CsrfForm/CsrfForm';
import { useSubscribeButtonLockContext } from '@/contexts/AppContext/SubscribeButtonLockContext';
import { useHintPopover } from '@/hooks/useHintPopover';
import { useMounted } from '@/hooks/useMounted';
import useTranslation from '@/hooks/useTranslation';
import type { IAnalyticsTraceableElement, ISeriesItem } from '@/interfaces';
import { trackEvent } from '@/services/Analytics/BBAnalytics';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import { toast } from '@/services/Toast/toastStore';
import subscriptionStatesScheduler from '@/services/UserActivity/SeriesSubscriptionStatesScheduler';
import classNames from '@/utils/classNames';
import type { ComponentProps, FC } from 'react';
import { useEffect, useId, useState } from 'react';
import styles from './SubscribeButton.module.css';
interface IProps extends IAnalyticsTraceableElement {
  series: ISeriesItem;
  initialSubscriptionState?: boolean;
}

/**
 * A button that maintains the subscription state of a series.
 */
export const SubscribeButton: FC<IProps & ComponentProps<typeof Button>> = ({
  className,
  series,
  initialSubscriptionState,
  analyticsBaseId = series.id,
  'aria-describedby': ariaDescribedBy,
  ...rest
}) => {
  const {
    t
  } = useTranslation('common');
  const isMounted = useMounted();
  const [isSubscribed, setIsSubscribed] = useState<boolean | undefined>(initialSubscriptionState);
  const [isLoading, setLoading] = useState(false);
  const {
    locked,
    setLocked
  } = useSubscribeButtonLockContext();
  const {
    popoverRef,
    openPopover,
    closePopover
  } = useHintPopover();
  const tooltipId = useId();
  const itemKey = series.id.toString();
  useEffect(() => subscriptionStatesScheduler.getInstance().registerItem(itemKey, x => {
    setIsSubscribed(x);
  }, initialSubscriptionState), [itemKey]);
  const onSubmitStart = () => {
    if (locked) {
      return false;
    }
    setLocked(true);
    setLoading(true);
  };
  const onSubmitEnd = (response: any) => {
    const isSubscribed_0 = response.isSubscribed;
    if (isMounted()) {
      setIsSubscribed(isSubscribed_0);
      setLoading(false);
    }
    const event = new CustomEvent('subscriptionState:change', {
      detail: {
        key: itemKey,
        value: isSubscribed_0
      }
    });
    document.dispatchEvent(event);
    trackEvent(isSubscribed_0 ? TrackingAction.Subscribe : TrackingAction.Unsubscribe, TrackingCategory.BnefContent, analyticsBaseId);
    toast.success(t(isSubscribed_0 ? 'SubscribeButton.subscribe_success' : 'SubscribeButton.unsubscribe_success'));
    setLocked(false);
  };
  const onSubmitError = () => {
    setLocked(false);
    toast.error(t('common:Forms.EditForm.save_error'));
    if (isMounted()) {
      setLoading(false);
    }
  };
  const icon = isSubscribed ? <BellActive /> : <BellInactive />;
  const text = t(isSubscribed ? 'SubscribeButton.unsubscribe' : 'SubscribeButton.subscribe');

  // Prefer a descriptive, series-specific tooltip; fall back to the generic one when no title is available.
  const tooltipText = t(isSubscribed ? 'SubscribeButton.tooltip_unsubscribe' : 'SubscribeButton.tooltip_subscribe', {
    seriesName: series.title
  });
  // Keep any caller-supplied description (e.g. a card title) and add the tooltip.
  const describedBy = [ariaDescribedBy, tooltipId].filter(Boolean).join(' ');
  return <CsrfForm className={classNames(className, popoverStyles.anchorScope)} apiUrl={`/series/${series.id}/subscription`} values={{
    action: isSubscribed ? 'unsubscribe' : 'subscribe'
  }} onSubmitStart={onSubmitStart} onSubmitEnd={onSubmitEnd} onSubmitError={onSubmitError}>
            <Button className={classNames(styles.button, popoverStyles.anchorTrigger)} kind="tertiary" icon={icon} isLoading={isLoading} type="submit" disabled={Boolean(isSubscribed === undefined || isLoading || locked)} {...rest} aria-describedby={describedBy} onMouseEnter={openPopover} onMouseLeave={closePopover} onFocus={openPopover} onBlur={closePopover}>
                {text}
            </Button>
            <span id={tooltipId} ref={popoverRef} role="tooltip" popover="hint" className={popoverStyles.popover}>
                <span className={popoverStyles.popoverInner}>{tooltipText}</span>
            </span>
        </CsrfForm>;
};